import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { getAllTenants } from '../services/tenantService';
import { getTenantStats } from '../services/adminRestaurantService';
import { Plus, AlertCircle } from 'lucide-react';
import type { Tenant } from 'foodhub-shared/types';

const TenantsPage: React.FC = () => {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [tenantStats, setTenantStats] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadTenants = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await getAllTenants();
        setTenants(data);

        // Load stats for each tenant
        const stats: Record<string, any> = {};
        for (const tenant of data) {
          const tenantStat = await getTenantStats(tenant.id);
          if (tenantStat) {
            stats[tenant.id] = tenantStat;
          }
        }
        setTenantStats(stats);
      } catch (err: any) {
        console.error('Error loading tenants:', err);
        setError(err.message || 'Failed to load restaurants');
      } finally {
        setLoading(false);
      }
    };

    loadTenants();
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'inactive':
        return 'bg-gray-100 text-gray-800';
      case 'suspended':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getPlanColor = (plan: string) => {
    switch (plan) {
      case 'basic':
        return 'bg-blue-100 text-blue-800';
      case 'pro':
        return 'bg-purple-100 text-purple-800';
      case 'enterprise':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <AdminLayout>
      <div className="p-8">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Restaurants</h1>
            <p className="text-gray-600 mt-2">Manage all restaurant tenants</p>
          </div>
          <button className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors">
            <Plus className="w-5 h-5" />
            <span>Add Restaurant</span>
          </button>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-red-800">{error}</p>
            </div>
          </div>
        )}

        {/* Tenants Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">All Restaurants ({tenants.length})</h2>
          </div>

          {loading ? (
            <div className="px-6 py-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Loading restaurants...</p>
            </div>
          ) : tenants.length === 0 ? (
            <div className="px-6 py-8 text-center">
              <p className="text-gray-500">No restaurants yet. Add your first restaurant to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Orders</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Revenue</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Tables</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Staff</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Plan</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-700 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {tenants.map((tenant) => {
                    const stats = tenantStats[tenant.id];
                    return (
                      <tr key={tenant.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 text-sm font-medium text-gray-900">{tenant.name}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{stats?.totalOrders || 0}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">RM {stats?.totalRevenue?.toFixed(2) || '0.00'}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{stats?.totalTables || 0}</td>
                        <td className="px-6 py-4 text-sm text-gray-600">{stats?.totalStaff || 0}</td>
                        <td className="px-6 py-4 text-sm">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPlanColor(tenant.subscriptionPlan)}`}>
                            {tenant.subscriptionPlan.charAt(0).toUpperCase() + tenant.subscriptionPlan.slice(1)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(tenant.status)}`}>
                            {tenant.status.charAt(0).toUpperCase() + tenant.status.slice(1)}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm">
                          <Link
                            to={`/tenants/${tenant.id}`}
                            className="text-blue-600 hover:text-blue-800 font-medium"
                          >
                            View
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default TenantsPage;

